import { Icon } from "@/components/ui/Icon";
import { useDateFormat } from "@/hooks/useDateFormat";
import { relDate, ticketAge, ticketAgeColor } from "@/lib/format";

// Shared ticket chips — the same look the RowDetailPanel header uses, so the
// human ID ("IT-42") and the age/lifetime chip ("seit 3 Tagen" / "gelöst in 6
// Std.") read identically across kanban cards, table rows, mobile cards,
// Postfach cards and Home open-work. Both no-op when their inputs are missing.

const ID_FONT = "JetBrains Mono Variable, JetBrains Mono, ui-monospace, monospace";

export function TicketIdChip({
  label,
  fontSize = 11.5,
}: {
  label: string | null | undefined;
  fontSize?: number;
}) {
  if (!label) return null;
  return (
    <span
      style={{
        fontSize,
        color: "var(--ink-3)",
        fontFamily: ID_FONT,
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// The row's due date, shown at-a-glance: overdue red, today amber, else neutral.
// "verspätet" (a completed-late item) isn't reachable here — open work only.
export function TicketDueChip({
  due,
  fontSize = 11.5,
}: {
  due: string | null | undefined;
  fontSize?: number;
}) {
  const [dateFormat] = useDateFormat();
  const rel = relDate(due, undefined, false, undefined, dateFormat);
  if (!rel) return null;
  const color = rel.overdue ? "var(--danger)" : rel.today ? "var(--warn)" : "var(--ink-2)";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize,
        fontWeight: 600,
        color,
        background: "var(--surface-soft)",
        borderRadius: 999,
        padding: "2px 9px",
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
      title={rel.label}
    >
      <Icon name="date" size={fontSize} color={color} />
      {rel.label}
    </span>
  );
}

export function TicketAgeChip({
  createdAt,
  resolvedAt,
}: {
  createdAt: string | null | undefined;
  resolvedAt: string | null | undefined;
}) {
  if (!createdAt) return null;
  const age = ticketAge(createdAt, resolvedAt ?? null);
  if (!age) return null;
  return (
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 600,
        color: ticketAgeColor(age.tone),
        background: "var(--surface-soft)",
        borderRadius: 999,
        padding: "2px 9px",
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      {age.label}
    </span>
  );
}
